/**
 * Admin — Egyedi (personalized) fizetési linkek.
 *
 * Létrehozás + listázás + revoke + email küldés. A link tokenét a szerver
 * generálja (32+ byte urlsafe random); soha nem szerkeszthető frontendről.
 * A fizetéskor a szerver a token alapján olvassa az árat → a felhasználó
 * nem tudja manipulálni az összeget.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Loader2, ChevronLeft, Plus, Copy, X, Mail, Trash2, ExternalLink,
  ClipboardList, QrCode, Download,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  adminCustomOfferCreate, adminCustomOfferList,
  adminCustomOfferRevoke, adminCustomOfferSendEmail,
} from '../services/api';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';

const STATUS_STYLE = {
  active:  { label: 'Aktív',      cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  used:    { label: 'Felhasznált', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  expired: { label: 'Lejárt',      cls: 'bg-gray-600/30 text-gray-400 border-gray-500/30' },
  revoked: { label: 'Visszavont',  cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
};

const fmtFull = (iso) => (iso ? new Date(iso).toLocaleString('hu-HU', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const fmtHUF  = (n) => (n == null ? '—' : `${Number(n).toLocaleString('hu-HU')} Ft`);

// Local yyyy-MM-ddTHH:mm string for a datetime-local input, 7 days ahead default.
const defaultExpiryLocal = () => {
  const d = new Date(Date.now() + 7 * 24 * 3600 * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function AdminCustomOffersPage() {
  const nav = useNavigate();
  const adminKey = localStorage.getItem(ADMIN_KEY_STORAGE) || '';
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [qrOffer, setQrOffer] = useState(null);   // offer | null

  const load = React.useCallback(() => {
    if (!adminKey) return;
    setLoading(true);
    adminCustomOfferList(adminKey)
      .then((d) => setOffers(d.offers || []))
      .catch((e) => toast.error('Hiba: ' + (e?.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, [adminKey]);

  useEffect(() => {
    if (!adminKey) { nav('/admin'); return; }
    load();
  }, [adminKey, nav, load]);

  const copyLink = async (link) => {
    try { await navigator.clipboard.writeText(link); toast.success('Link vágólapra másolva.'); }
    catch { toast.error('Nem sikerült a másolás.'); }
  };

  const revoke = async (offer) => {
    if (!window.confirm(`Biztosan visszavonod ezt az ajánlatot?\n\n${offer.title}\n${fmtHUF(offer.custom_price)}`)) return;
    try {
      await adminCustomOfferRevoke(adminKey, offer.id);
      toast.success('Ajánlat visszavonva.');
      load();
    } catch (e) {
      toast.error('Sikertelen: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const sendEmail = async (offer) => {
    if (!offer.customer_email) { toast.error('Nincs megadva az ügyfél email címe.'); return; }
    try {
      const r = await adminCustomOfferSendEmail(adminKey, offer.id);
      toast.success(`Email elküldve — ${r.email_status}`);
      load();
    } catch (e) {
      toast.error('Sikertelen: ' + (e?.response?.data?.detail || e.message));
    }
  };

  if (!adminKey) return null;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-gray-400 hover:text-white"><ChevronLeft size={20} /></Link>
            <ClipboardList className="text-yellow-500" size={20} />
            <h1 className="text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Egyedi ajánlatok</h1>
          </div>
          <Button
            data-testid="new-offer-btn"
            onClick={() => setDialogOpen(true)}
            className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold"
          >
            <Plus size={16} className="mr-1.5" /> Új egyedi ajánlat
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <div className="max-w-3xl mb-6 p-4 rounded-lg bg-yellow-500/5 border border-yellow-500/20 text-sm text-gray-300">
          <p className="mb-1"><span className="font-semibold text-yellow-300">Mire való?</span> Kivételes, egyedi árazású esetekre — konzultáció után egyedileg beállított csomagok (pl. családi ajánlat). A nyilvános árlista változatlan marad; az egyedi ár csak a privát linken elérhető.</p>
          <p><span className="font-semibold text-yellow-300">Biztonság:</span> A link tokenét a szerver generálja (kriptográfiai random), az árat a szerver olvassa fizetéskor — a frontend nem tudja módosítani. Sikeres fizetés után a link automatikusan érvénytelenné válik.</p>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-gray-500 py-8">
            <Loader2 className="animate-spin" size={16} /> Töltés…
          </div>
        )}

        {!loading && offers.length === 0 && (
          <div className="bg-gray-900 border border-dashed border-gray-700 rounded-lg p-16 text-center text-gray-500 max-w-2xl">
            Még nincs egyedi ajánlat. Kattints a jobb felső <span className="text-yellow-400 font-semibold">„Új egyedi ajánlat"</span> gombra.
          </div>
        )}

        {!loading && offers.length > 0 && (
          <div className="space-y-3">
            {offers.map((o) => {
              const style = STATUS_STYLE[o.status] || STATUS_STYLE.active;
              return (
                <div
                  key={o.id}
                  data-testid={`offer-row-${o.id}`}
                  className="bg-gray-900 border border-gray-800 rounded-lg p-4 md:p-5"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest border ${style.cls}`}>
                          {style.label}
                        </span>
                        <h3 className="text-base md:text-lg font-bold text-white">{o.title}</h3>
                      </div>
                      <div className="mt-2 text-2xl font-bold text-yellow-400">{fmtHUF(o.custom_price)}</div>
                      <div className="mt-1 text-xs text-gray-400 space-y-0.5">
                        {o.customer_name && <div>Ügyfél: <span className="text-gray-200">{o.customer_name}</span>{o.customer_email && <> · <a href={`mailto:${o.customer_email}`} className="text-yellow-400 hover:underline">{o.customer_email}</a></>}</div>}
                        <div>Lejárat: <span className="text-gray-200">{fmtFull(o.expires_at)}</span></div>
                        <div>Létrehozva: <span className="text-gray-200">{fmtFull(o.created_at)}</span>
                          {o.used_at && <> · Felhasználva: <span className="text-gray-200">{fmtFull(o.used_at)}</span></>}
                          {o.email_sent_at && <> · Email küldve: <span className="text-gray-200">{fmtFull(o.email_sent_at)}</span></>}
                        </div>
                        {o.internal_note && <div className="italic text-gray-500">Belső: {o.internal_note}</div>}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-800 flex flex-wrap items-center gap-2">
                    <div className="flex-1 min-w-0 bg-gray-800/50 border border-gray-700 rounded px-3 py-1.5 text-xs text-gray-300 font-mono truncate">
                      {o.link || '—'}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyLink(o.link)}
                      data-testid={`offer-copy-${o.id}`}
                      className="border-gray-700 text-gray-300 hover:text-yellow-400"
                    >
                      <Copy size={13} className="mr-1" /> Másolás
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setQrOffer(o)}
                      data-testid={`offer-qr-${o.id}`}
                      className="border-gray-700 text-gray-300 hover:text-yellow-400"
                    >
                      <QrCode size={13} className="mr-1" /> QR kód
                    </Button>
                    <a
                      href={o.link}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-yellow-400 px-2 py-1.5"
                    >
                      <ExternalLink size={13} /> Megnyitás
                    </a>
                    {o.status === 'active' && o.customer_email && (
                      <Button
                        size="sm"
                        onClick={() => sendEmail(o)}
                        data-testid={`offer-email-${o.id}`}
                        className="bg-blue-600 hover:bg-blue-500 text-white"
                      >
                        <Mail size={13} className="mr-1" /> Küldés emailben
                      </Button>
                    )}
                    {o.status === 'active' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => revoke(o)}
                        data-testid={`offer-revoke-${o.id}`}
                        className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 size={13} className="mr-1" /> Visszavonás
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <QrDialog offer={qrOffer} onClose={() => setQrOffer(null)} />

      {dialogOpen && (
        <NewOfferDialog
          adminKey={adminKey}
          onClose={() => setDialogOpen(false)}
          onCreated={() => { setDialogOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function QrDialog({ offer, onClose }) {
  const canvasWrapRef = React.useRef(null);

  const downloadPng = () => {
    const canvas = canvasWrapRef.current?.querySelector('canvas');
    if (!canvas) { toast.error('A QR kód még nem töltött be.'); return; }
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `qr-${(offer.title || 'ajanlat').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)}.png`;
    a.click();
    toast.success('QR kód letöltve (PNG).');
  };

  return (
    <Dialog open={!!offer} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent data-testid="offer-qr-dialog" className="bg-white text-gray-900 sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-gray-900 text-lg font-bold">QR kód a fizetéshez</DialogTitle>
          <DialogDescription className="text-gray-500 text-xs">
            A vendég beolvassa a telefonjával és azonnal fizet — terminál nélkül.
          </DialogDescription>
        </DialogHeader>
        {offer && (
          <div className="text-center">
            <div ref={canvasWrapRef} className="bg-white p-3 rounded-lg inline-block border-2 border-gray-200">
              <QRCodeCanvas value={offer.link || ''} size={240} level="M" includeMargin />
            </div>
            <div className="mt-3 font-bold text-gray-900">{offer.title}</div>
            <div className="text-xl font-bold text-yellow-600">{fmtHUF(offer.custom_price)}</div>
            <div className="text-[11px] text-gray-500 mt-2 break-all">{offer.link}</div>
            <Button
              onClick={downloadPng}
              data-testid="offer-qr-download"
              className="mt-4 w-full bg-gray-900 hover:bg-gray-800 text-white font-bold"
            >
              <Download size={14} className="mr-1.5" /> PNG letöltése (nyomtatható)
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function NewOfferDialog({ adminKey, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: '',
    custom_price: '',
    customer_name: '',
    customer_email: '',
    internal_note: '',
    expires_at_local: defaultExpiryLocal(),
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title || form.title.trim().length < 2) { toast.error('Add meg a program/tétel nevét.'); return; }
    if (!form.custom_price || Number(form.custom_price) < 100) { toast.error('Add meg az egyedi árat (min. 100 Ft).'); return; }
    if (!form.expires_at_local) { toast.error('Add meg a lejárati időt.'); return; }
    // Convert local datetime-local input to ISO with local offset -> UTC
    const dt = new Date(form.expires_at_local);
    if (isNaN(dt.getTime()) || dt.getTime() <= Date.now()) {
      toast.error('A lejárati időpontnak a jövőben kell lennie.'); return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        custom_price: Number(form.custom_price),
        expires_at: dt.toISOString(),
        single_use: true,
      };
      if (form.customer_name.trim())  payload.customer_name  = form.customer_name.trim();
      if (form.customer_email.trim()) payload.customer_email = form.customer_email.trim();
      if (form.internal_note.trim())  payload.internal_note  = form.internal_note.trim();
      await adminCustomOfferCreate(adminKey, payload);
      toast.success('Egyedi ajánlat létrehozva.');
      onCreated?.();
    } catch (e) {
      toast.error('Sikertelen: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent data-testid="new-offer-dialog" className="bg-gray-900 border-gray-700 text-white sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white">Új egyedi ajánlat</DialogTitle>
          <DialogDescription className="text-gray-400">
            A létrehozás után egy privát, csak neked megjelenő linket kapsz, amit átküldhetsz az ügyfélnek.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-gray-300 text-xs">Program / tétel neve *</Label>
            <Input
              data-testid="offer-title"
              value={form.title} onChange={set('title')}
              placeholder="pl. Családi csomag – 2 felnőtt + 1 gyerek"
              className="bg-gray-800 border-gray-700 text-white h-10 mt-1"
            />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Egyedi ár (Ft) *</Label>
            <Input
              data-testid="offer-price"
              type="number" min="100" step="500"
              value={form.custom_price} onChange={set('custom_price')}
              placeholder="150000"
              className="bg-gray-800 border-gray-700 text-white h-10 mt-1"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-300 text-xs">Ügyfél neve (opcionális)</Label>
              <Input
                data-testid="offer-customer-name"
                value={form.customer_name} onChange={set('customer_name')}
                placeholder="Kiss Család"
                className="bg-gray-800 border-gray-700 text-white h-10 mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-300 text-xs">Email (opcionális)</Label>
              <Input
                data-testid="offer-customer-email"
                type="email"
                value={form.customer_email} onChange={set('customer_email')}
                placeholder="kiss@example.com"
                className="bg-gray-800 border-gray-700 text-white h-10 mt-1"
              />
            </div>
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Belső megjegyzés (csak neked)</Label>
            <Textarea
              data-testid="offer-note"
              value={form.internal_note} onChange={set('internal_note')}
              placeholder="pl. konzultáció után egyeztetve, hosszú távú elköteleződés"
              rows={2}
              className="bg-gray-800 border-gray-700 text-white mt-1"
            />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Lejárat *</Label>
            <Input
              data-testid="offer-expiry"
              type="datetime-local"
              value={form.expires_at_local} onChange={set('expires_at_local')}
              className="bg-gray-800 border-gray-700 text-white h-10 mt-1"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-gray-700 text-gray-300">
            <X size={14} className="mr-1" /> Mégse
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="offer-submit"
            className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold"
          >
            {saving ? <><Loader2 className="animate-spin mr-1.5" size={14} /> Létrehozás…</> : <><Plus size={14} className="mr-1.5" /> Létrehozás</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
